import { useIsMobileClient } from '@/hooks/common/use-device-type'
import {
  ColorMapperList,
  ProductAttribute,
  TCollectionFormatted,
} from '@/types/collection'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import { sortedAttributeCardsUnderwear } from '../../constants'
import { useCollectionStore } from '../../store/collection-store'
import { objectToSearchParams } from '../../utils/filter-tag-utils'
import {
  buildFilterParams,
  getFilterValue as getValue,
  getFilterValues as getValues,
  parseFiltersFromSearchParams,
  updateUrl,
} from '../../utils/filter-utils'
import { EXCLUDE_PATH_CONCAT_COLOR } from './constants'

interface UseFilterStateProps {
  onClose?: () => void
  collection: TCollectionFormatted
}

export const useFilterState = ({
  onClose,
  collection,
}: UseFilterStateProps) => {
  const isMobile = useIsMobileClient()
  const selectedFilters = useCollectionStore((s) => s.filter)
  const setSelectedFilters = useCollectionStore((s) => s.setFilter)
  const searchParams = useSearchParams()
  const pathname = usePathname()

  const router = useRouter()

  const [localFilter, setLocalFilter] =
    useState<Record<string, string | string[] | undefined>>(selectedFilters)

  useEffect(() => {
    const currentFilters = parseFiltersFromSearchParams(searchParams)
    const { request } = collection || {}
    const { color, concat, att } = request || {}

    const nextFilters: Record<string, string | string[] | undefined> = {
      ...(color ? { color: concat } : {}),
    }

    // Merge att và currentFilters
    if (att && typeof att === 'object' && !Array.isArray(att)) {
      Object.entries(att).forEach(([key, value]) => {
        const currentValue = currentFilters[key]
        if (currentValue) {
          // Nếu cả hai đều là string, chuyển thành array và merge
          const attValues =
            typeof value === 'string'
              ? [value]
              : Array.isArray(value)
                ? value
                : []
          const currentValues =
            typeof currentValue === 'string'
              ? [currentValue]
              : Array.isArray(currentValue)
                ? currentValue
                : []

          // Merge và unique values
          const mergedValues = [...new Set([...attValues, ...currentValues])]
          nextFilters[key] =
            mergedValues.length === 1 ? mergedValues[0] : mergedValues
        } else {
          nextFilters[key] = value
        }
      })
    }

    // Thêm các giá trị từ currentFilters mà không có trong att
    Object.entries(currentFilters).forEach(([key, value]) => {
      if (!nextFilters[key]) {
        nextFilters[key] = value
      }
    })

    setSelectedFilters(nextFilters)
  }, [collection, searchParams]) // Empty dependency array để chỉ chạy 1 lần khi mount

  // Thay đổi filter (local hoặc global tuỳ thiết bị)
  const changeFilter = (paramName: string, value: string | string[]) => {
    const nextFilters = {
      ...(isMobile ? localFilter : selectedFilters),
    }

    if (Array.isArray(value) && value.length === 0) {
      delete nextFilters[paramName]
    } else {
      nextFilters[paramName] = value
    }

    if (isMobile) {
      setLocalFilter(nextFilters)
    } else {
      setSelectedFilters(nextFilters)
      applyFilter(nextFilters)
    }
  }

  // Áp dụng filter (dùng cho mobile khi bấm nút "Áp dụng")
  const applyFilter = (
    nextFilters: Record<string, string | string[] | undefined>
  ) => {
    console.log('🔄 applyFilter called with:', nextFilters)
    const params = buildFilterParams(nextFilters, searchParams)
    // const currentUrl = `${pathname}?${params.toString()}`
    const currentParams = objectToSearchParams(selectedFilters)
    console.log('📝 Calling processSeoRedirect with:', {
      pathname,
      params: params.toString(),
      currentParams: currentParams.toString(),
    })
    const newUrl = processSeoRedirect(
      pathname,
      collection,
      params,
      currentParams
    )
    const newPath = newUrl.split('?')[0]
    console.log('🔄 URL processing result:', {
      oldUrl: window.location.pathname + window.location.search,
      newUrl,
      newPath,
      willNavigate:
        window.location.pathname + window.location.search !== newUrl,
    })

    if (window.location.pathname + window.location.search !== newUrl) {
      if (newPath !== window.location.pathname) {
        console.log('🚀 Navigating to new path:', newUrl)
        router.push(newUrl, { scroll: false })
      } else {
        console.log('🔄 Updating URL in place:', newUrl)
        updateUrl(newUrl, { scroll: false })
      }

      setSelectedFilters(nextFilters)
    }
    onClose?.()
  }

  // Xoá filter
  const clearFilter = () => {
    setLocalFilter({})
    setSelectedFilters({})
    updateUrl(pathname, { scroll: false })
    onClose?.()
  }

  // Lấy giá trị filter dạng string
  const getFilterValue = (key: string): string | undefined => {
    return getValue(key, isMobile ? localFilter : selectedFilters)
  }

  // Lấy giá trị filter dạng array
  const getFilterValues = (key: string): string[] => {
    return getValues(key, isMobile ? localFilter : selectedFilters)
  }

  return {
    localFilter,
    setLocalFilter,
    changeFilter,
    applyFilter,
    clearFilter,
    getFilterValue,
    getFilterValues,
  }
}

const extractMatchedSlugsFromPath = (
  pathname: string,
  allConcatSlugs: string[]
) => {
  const lastSegment = pathname.split('/').pop()?.split('?')[0] ?? ''

  return (
    allConcatSlugs
      .sort((a, b) => b.length - a.length)
      .find((slug) => lastSegment.endsWith(slug)) ?? ''
  )
}

const PARAMS_TO_REMOVE = ['itm_source']

// Helper function to get concat slug attributes
const getConcatSlugAttributes = (
  collection: TCollectionFormatted,
  isUnderwear: boolean
): ProductAttribute[] => {
  const underWareAttributes: ProductAttribute[] = [
    {
      _id: 'quan-lot-nam',
      title: 'Quần lót nam',
      description: 'Quần lót nam',
      available: true,
      updated_at: new Date(),
      created_at: new Date(),
      seo_alias: 'kieu-dang-quan-lot',
      allow_multiple: false,
      concat_slug: true,
      priority: '1',
      items: sortedAttributeCardsUnderwear.map((item) => ({
        ...item,
        updated_at: item.updated_at.toISOString(),
        created_at: item.created_at.toISOString(),
        short_description: item.short_description || '',
        sort: item.sort.toString(),
      })),
    },
  ]

  return [
    ...(collection?.filter?.attributes?.filter((attr) => attr.concat_slug) ||
      []),
    ...(collection?.filter?.attributeCards
      ?.filter((attr) => attr.concat_slug)
      .map((attr) => ({
        ...attr,
        items: attr.items.map((item) => ({
          ...item,
          updated_at: item?.updated_at?.toISOString?.() || '',
          created_at: item?.created_at?.toISOString?.() || '',
          sort: item?.sort?.toString(),
          short_description: item?.short_description || '',
        })),
      })) || []),
    ...(isUnderwear ? underWareAttributes : []),
  ]
}

// Helper function to get all slugs
const getAllSlugs = (
  colorList: ColorMapperList[],
  concatSlugAttributes: ProductAttribute[]
): string[] => {
  return [
    ...colorList.map((c) => c.seo_alias),
    ...concatSlugAttributes.flatMap((attr) =>
      (attr.items || []).map((item) => item.seo_alias)
    ),
  ]
}

// Helper function to find current concat attribute in path
const findCurrentConcatAttributeInPath = (
  pathname: string,
  concatSlugAttributes: ProductAttribute[]
): ProductAttribute | undefined => {
  return concatSlugAttributes.find((attr) => {
    return attr.items?.some((item) => {
      return pathname.endsWith(`${item.seo_alias}`)
    })
  })
}

// Helper function to handle color in query when no concat attribute in path
const handleColorInQueryWithoutConcatAttribute = (
  pathname: string,
  colorInQuery: string,
  colorList: ColorMapperList[],
  currentConcat: string,
  nextParams: URLSearchParams
): string => {
  console.log('🎨 handleColorInQueryWithoutConcatAttribute:', {
    pathname,
    colorInQuery,
    currentConcat,
    colorListLength: colorList.length,
  })

  const colorInPath = colorList.find((c) => c.seo_alias === currentConcat)
  console.log('🔍 Color in path found:', colorInPath?.seo_alias)

  if (colorInPath?.seo_alias !== colorInQuery && colorInPath) {
    console.log(
      '🔄 Replacing color in path:',
      `${currentConcat} → ${colorInQuery}`
    )
    const newPath = pathname.replace(`-${currentConcat}`, `-${colorInQuery}`)
    nextParams.delete('color')
    const params = nextParams.toString()
    const result = `${newPath}${params ? `?${params}` : ''}`
    console.log('✅ Color replacement result:', result)
    return result
  }

  const colorQuery = colorList.find((c) => c.seo_alias === colorInQuery)
  console.log('🔍 Color query found:', colorQuery?.seo_alias)
  if (colorQuery?.seo_alias !== currentConcat && colorQuery) {
    console.log('➕ Adding color to path:', colorQuery.seo_alias)
    nextParams.delete('color')
    const params = nextParams.toString()
    const result = `${pathname}-${colorQuery.seo_alias}${params ? `?${params}` : ''}`
    console.log('✅ Color addition result:', result)
    return result
  }

  if (colorInPath?.seo_alias === colorInQuery) {
    console.log('✅ Color already matches, removing from query')
    nextParams.delete('color')
    const params = nextParams.toString()
    const result = `${pathname}${params ? `?${params}` : ''}`
    console.log('✅ Color match result:', result)
    return result
  }

  const result = `${pathname}?${nextParams.toString()}`
  console.log('🔄 No color changes needed:', result)
  return result
}

// Helper function to handle color removal
const handleColorRemoval = (
  pathname: string,
  colorInPath: ColorMapperList,
  nextParams: URLSearchParams,
  concatSlugAttributes: ProductAttribute[]
): string => {
  nextParams.delete('color')
  const attributeInQuery = concatSlugAttributes.find((attr) => {
    return nextParams?.get(attr.seo_alias)
  })
  const currentQueryAttribute = nextParams
    .get(attributeInQuery?.seo_alias || '')
    ?.split(',')?.[0]

  if (currentQueryAttribute) {
    const newPath = pathname.replace(
      `${colorInPath.seo_alias}`,
      currentQueryAttribute
    )

    const queryAttributes = nextParams
      .get(attributeInQuery?.seo_alias || '')
      ?.split(',')
      .filter((item) => item !== currentQueryAttribute)
      .join(',')

    console.log('🧙 ~ queryAttributes:', queryAttributes)
    if (queryAttributes) {
      nextParams.set(attributeInQuery?.seo_alias || '', queryAttributes)
    } else {
      nextParams.delete(attributeInQuery?.seo_alias || '')
    }

    const params = nextParams.toString()
    return `${newPath}${params ? `?${params}` : ''}`
  }

  const newPath = pathname.replace(`-${colorInPath.seo_alias}`, '')
  const params = nextParams.toString()
  return `${newPath}${params ? `?${params}` : ''}`
}

// Helper function to handle concat attribute in path
const handleConcatAttributeInPath = (
  pathname: string,
  currentConcatAttributeInPath: ProductAttribute,
  currentConcat: string,
  nextParams: URLSearchParams,
  currentParams?: URLSearchParams
): string => {
  console.log('🔧 handleConcatAttributeInPath:', {
    pathname,
    attributeSeoAlias: currentConcatAttributeInPath.seo_alias,
    currentConcat,
    nextParams: nextParams.toString(),
    currentParams: currentParams?.toString(),
  })

  const currentValue = nextParams.get(
    currentConcatAttributeInPath?.seo_alias || ''
  )
  console.log('🔍 Current value in nextParams:', currentValue)

  const item = currentConcatAttributeInPath.items?.find(
    (item) => item.seo_alias === currentValue
  )
  console.log('🔍 Found item for current value:', item?.seo_alias)

  if (item && item?.seo_alias !== currentConcat) {
    console.log(
      '🔄 Replacing concat in path:',
      `${currentConcat} → ${item.seo_alias}`
    )

    const queryAttributes = nextParams
      ?.get(currentConcatAttributeInPath.seo_alias)
      ?.split(',')
      .filter((value) => value !== item?.seo_alias)
      .join(',')

    if (queryAttributes) {
      nextParams.set(currentConcatAttributeInPath.seo_alias, queryAttributes)
    } else {
      nextParams.delete(currentConcatAttributeInPath.seo_alias)
    }

    const newPath = pathname.replace(`-${currentConcat}`, `-${item?.seo_alias}`)
    const result = `${newPath}${nextParams ? `?${nextParams.toString()}` : ''}`
    console.log('✅ Concat replacement result:', result)
    return result
  }

  const preAttribute = currentParams?.get(
    currentConcatAttributeInPath.seo_alias
  )
  console.log('📝 Previous attribute value:', preAttribute)

  if (preAttribute && !nextParams.get(currentConcatAttributeInPath.seo_alias)) {
    console.log('🗑️ Handling attribute removal')
    return handleAttributeRemoval(
      pathname,
      preAttribute,
      currentConcatAttributeInPath,
      nextParams
    )
  }

  if (nextParams.get(currentConcatAttributeInPath.seo_alias)) {
    console.log('🔄 Handling attribute update')
    return handleAttributeUpdate(
      pathname,
      currentConcatAttributeInPath,
      currentConcat,
      nextParams,
      currentParams
    )
  }

  const result = `${pathname}?${nextParams.toString()}`
  console.log('🔄 No changes needed in handleConcatAttributeInPath:', result)
  return result
}

// Helper function to handle attribute removal
const handleAttributeRemoval = (
  pathname: string,
  preAttribute: string,
  currentConcatAttributeInPath: ProductAttribute,
  nextParams: URLSearchParams
): string => {
  const itemPrevArrValues = preAttribute?.split(',').find((item) => {
    return pathname.endsWith(`-${item}`)
  })

  if (itemPrevArrValues) {
    const newPath = pathname.replace(`-${itemPrevArrValues}`, '')
    nextParams.delete(currentConcatAttributeInPath.seo_alias)
    const params = nextParams.toString()
    return `${newPath}${params ? `?${params}` : ''}`
  }

  const newPath = pathname.replace(`-${preAttribute}`, '')
  nextParams.delete(currentConcatAttributeInPath.seo_alias)
  const params = nextParams.toString()
  return `${newPath}${params ? `?${params}` : ''}`
}

// Helper function to handle attribute update
const handleAttributeUpdate = (
  pathname: string,
  currentConcatAttributeInPath: ProductAttribute,
  currentConcat: string,
  nextParams: URLSearchParams,
  currentParams?: URLSearchParams
): string => {
  const currentParamsValue = currentParams?.get(
    currentConcatAttributeInPath.seo_alias
  )
  const nextParamsValue = nextParams.get(currentConcatAttributeInPath.seo_alias)

  if (currentParamsValue) {
    const currentValues = currentParamsValue.split(',')
    const nextValues = nextParamsValue?.split(',') || []
    const removedValues = currentValues.filter(
      (value) => !nextValues.includes(value)
    )

    for (const value of removedValues) {
      if (pathname.endsWith(`-${value}`)) {
        const newPath = pathname.replace(`-${value}`, '')
        const params = nextParams.toString()
        return `${newPath}${params ? `?${params}` : ''}`
      }
    }
  }

  const itemInPath = currentConcatAttributeInPath.items?.find(
    (item) => item.seo_alias === currentConcat
  )

  const attributeInQuery = nextParams.get(
    currentConcatAttributeInPath.seo_alias
  )
  const queryAttributes = attributeInQuery
    ?.split(',')
    .filter((item) => item !== itemInPath?.seo_alias)
    .join(',')

  if (queryAttributes) {
    nextParams.set(currentConcatAttributeInPath.seo_alias, queryAttributes)
  } else {
    nextParams.delete(currentConcatAttributeInPath.seo_alias)
  }

  const params = nextParams.toString()
  return `${pathname}${params ? `?${params}` : ''}`
}

// Helper function to handle new attribute in query
const handleNewAttributeInQuery = (
  pathname: string,
  attributeInQuery: ProductAttribute,
  nextParams: URLSearchParams
): string => {
  console.log('➕ handleNewAttributeInQuery:', {
    pathname,
    attributeSeoAlias: attributeInQuery.seo_alias,
    nextParams: nextParams.toString(),
  })

  const currentQuery = nextParams
    .get(attributeInQuery.seo_alias)
    ?.split(',')?.[0]
  console.log('🔍 First value in query:', currentQuery)

  const item = attributeInQuery.items?.find(
    (item) => item.seo_alias === currentQuery
  )
  console.log('🔍 Found item for query:', item?.seo_alias)

  const queryAttributes = nextParams
    .get(attributeInQuery?.seo_alias || '')
    ?.split(',')
    .filter((item) => item !== currentQuery)
    .join(',')
  console.log('📝 Remaining query attributes:', queryAttributes)

  if (queryAttributes) {
    nextParams.set(attributeInQuery?.seo_alias || '', queryAttributes)
  } else {
    nextParams.delete(attributeInQuery?.seo_alias || '')
  }

  const params = nextParams.toString()
  const result = `${pathname}-${item?.seo_alias}${params ? `?${params}` : ''}`
  console.log('✅ handleNewAttributeInQuery result:', result)
  return result
}

export const processSeoRedirect = (
  pathname: string,
  collection: TCollectionFormatted,
  nextParams: URLSearchParams,
  currentParams?: URLSearchParams
): string => {
  console.log('🚀 processSeoRedirect START', {
    pathname,
    nextParams: nextParams.toString(),
    currentParams: currentParams?.toString(),
    collectionId: collection.detail._id,
  })

  // Handle excluded paths
  if (EXCLUDE_PATH_CONCAT_COLOR.includes(pathname)) {
    console.log('⚠️ Excluded path detected:', pathname)
    PARAMS_TO_REMOVE.forEach((param) => {
      nextParams.delete(param)
    })
    const params = nextParams.toString()
    const result = `${pathname}${params ? `?${params}` : ''}`
    console.log('✅ Excluded path result:', result)
    return result
  }

  const isUnderwear = collection.filter.isUnderwearCollection ?? false
  const colorList = collection?.variantInfo?.colorMapperList || []
  const concatSlugAttributes = getConcatSlugAttributes(collection, isUnderwear)
  const allSlugs = getAllSlugs(colorList, concatSlugAttributes)
  const currentConcat = extractMatchedSlugsFromPath(pathname, allSlugs)
  const colorInQuery = nextParams.get('color')
  const currentConcatAttributeInPath = findCurrentConcatAttributeInPath(
    pathname,
    concatSlugAttributes
  )

  console.log('📊 processSeoRedirect variables:', {
    isUnderwear,
    colorListLength: colorList.length,
    concatSlugAttributesLength: concatSlugAttributes.length,
    allSlugsLength: allSlugs.length,
    currentConcat,
    colorInQuery,
    hasCurrentConcatAttributeInPath: !!currentConcatAttributeInPath,
    currentConcatAttributeInPathSeoAlias:
      currentConcatAttributeInPath?.seo_alias,
  })

  // Handle case when no concat attribute in path
  if (!currentConcatAttributeInPath) {
    console.log('🔄 No concat attribute in path')
    if (colorInQuery) {
      console.log('🎨 Color in query detected:', colorInQuery)
      const result = handleColorInQueryWithoutConcatAttribute(
        pathname,
        colorInQuery,
        colorList,
        currentConcat,
        nextParams
      )
      console.log('✅ handleColorInQueryWithoutConcatAttribute result:', result)
      return result
    }

    const colorInPath = colorList.find((c) => c.seo_alias === currentConcat)
    console.log('🎨 Color in path:', colorInPath?.seo_alias)
    if (colorInPath) {
      const preColor = currentParams?.get('color')
      const nextColor = nextParams.get('color')
      console.log('🔄 Color removal check:', { preColor, nextColor })

      if ((preColor && !nextColor) || (!preColor && !nextColor)) {
        console.log('🗑️ Handling color removal')
        const result = handleColorRemoval(
          pathname,
          colorInPath,
          nextParams,
          concatSlugAttributes
        )
        console.log('✅ handleColorRemoval result:', result)
        return result
      }
    }
  }

  // Handle case when concat attribute exists in path
  if (currentConcatAttributeInPath) {
    console.log(
      '🔧 Concat attribute exists in path:',
      currentConcatAttributeInPath.seo_alias
    )
    const result = handleConcatAttributeInPath(
      pathname,
      currentConcatAttributeInPath,
      currentConcat,
      nextParams,
      currentParams
    )
    console.log('✅ handleConcatAttributeInPath result:', result)
    return result
  }

  // Handle new attribute in query
  const attributeInQuery = concatSlugAttributes.find((attr) => {
    return nextParams?.get(attr.seo_alias)
  })
  console.log('🔍 Attribute in query:', attributeInQuery?.seo_alias)

  const isRemoveAttribute =
    attributeInQuery && !nextParams.get(attributeInQuery.seo_alias)
  console.log('🗑️ Is remove attribute:', isRemoveAttribute)

  if (attributeInQuery && !isRemoveAttribute) {
    console.log('➕ Handling new attribute in query')
    const result = handleNewAttributeInQuery(
      pathname,
      attributeInQuery,
      nextParams
    )
    console.log('✅ handleNewAttributeInQuery result:', result)
    return result
  }

  // Default return
  const params = nextParams.toString()
  const result = `${pathname}${params ? `?${params}` : ''}`
  console.log('🏁 Default return result:', result)
  return result
}

export const processSeoRedirectRemoveTag = (
  pathname: string,
  collection: TCollectionFormatted,
  nextParams: URLSearchParams,
  currentParams: URLSearchParams
) => {
  const newUrl = processSeoRedirect(
    pathname,
    collection,
    nextParams,
    currentParams
  )

  // return `${pathname}?${currentParams.toString()}`

  return newUrl
}
